import { AUTH_STORAGE_KEY } from "~/constants";
import config from "./config";
import { persisterStorage } from "./persist-storage";
import { toast } from "sonner";
import axios from "axios";


let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
}

// Helper function for reliable redirects on all devices including iOS
function redirectToLogin() {
  if (typeof window === "undefined") return;

  // Clear auth data first
  persisterStorage.removeItem(AUTH_STORAGE_KEY);

  // Determine the program from current path
  const currentPath = window.location.pathname;
  const programMatch = currentPath.match(/(weight-loss|urgent-care)/);
  const program = programMatch ? programMatch[1] : 'weight-loss';
  const redirectUrl = `/${program}`;

  // Prevent infinite loops if already on the target page
  if (currentPath === redirectUrl) {
    return;
  }

  // Use multiple redirect strategies for better iOS compatibility
  // Strategy 1: Try immediate location replace (works best on iOS)
  try {
    window.location.replace(redirectUrl);
  } catch (e) {
    // Strategy 2: Fallback to href assignment
    try {
      window.location.href = redirectUrl;
    } catch (e2) {
      // Strategy 3: Last resort - force reload to program path
      window.location.pathname = redirectUrl;
    }
  }
}

if (!config.apiUrl) {
  console.warn("Missing API URL configuration. Please set a valid API URL in your configuration.");
}

type FetchFunction = typeof fetch;

const PUBLIC_ENDPOINTS = ["/admin/authentication", "/admin/reset-password", "admin/reset-password-request"] as const;

const isPublicEndpoint = (url: string | URL | Request): boolean => {
  const urlString = url instanceof Request ? url.url : url.toString();

  const path = urlString.startsWith('/') ? urlString : new URL(urlString).pathname;

  return PUBLIC_ENDPOINTS.some((endpoint) => path === endpoint);
};

const createAuthFetch = (baseFetch: FetchFunction): FetchFunction => {
  return async (input, init) => {
    try {
      const url = input instanceof Request ?
        new URL(input.url, config.apiUrl) :
        new URL(input.toString(), config.apiUrl);

      if (isPublicEndpoint(url.pathname)) {
        const headers = new Headers(init?.headers);

        return await baseFetch(url, {
          ...init,
          credentials: 'include',
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
        });
      }

      const token = persisterStorage.getItem(AUTH_STORAGE_KEY);
      const headers = new Headers(init?.headers);

      const response = await baseFetch(url, {
        ...init,
        credentials: 'include',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token || ""}`,
          ...headers,
        },
      });

      // Handle 401 errors with token refresh
      if (response.status === 401) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((newToken) => {
            return baseFetch(url, {
              ...init,
              credentials: 'include',
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${newToken}`,
                ...headers,
              },
            });
          });
        }

        isRefreshing = true;

        try {
          const refreshResponse = await axios.post('/admin/refresh', {}, {
            withCredentials: true,
          });

          const newToken = refreshResponse.data?.access_token;

          if (newToken) {
            persisterStorage.setItem(AUTH_STORAGE_KEY, newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            processQueue(null, newToken);

            return baseFetch(url, {
              ...init,
              credentials: 'include',
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${newToken}`,
                ...headers,
              },
            });
          } else {
            throw new Error('No token received from refresh');
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          const errorData = await response.clone().json().catch(() => ({}));
          // Only redirect if it looks like a token issue
          if (errorData.detail === "Token has expired" || errorData.detail === "Invalid token" || errorData.detail === "Refresh token missing" || response.status === 401) {
            toast.error("Session expired. Please login again.");
            redirectToLogin();
          }
          return response;
        } finally {
          isRefreshing = false;
        }
      }

      return response;
    } catch (error) {
      // Handle different types of network errors with user-friendly messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error("Unable to connect to server. Please check your internet connection.");
        throw new Error("Unable to connect to server. Please check your internet connection.");
      } else if (error instanceof Error && error.message.includes('NetworkError')) {
        toast.error("Network error occurred. Please try again.");
        throw new Error("Network error occurred. Please try again.");
      } else if (error instanceof Error) {
        toast.error("Something went wrong. Please try again.");
        throw new Error("Something went wrong. Please try again.");
      }

      throw error;
    }
  };
};

export const apiClient = createAuthFetch(fetch);

axios.defaults.baseURL = config.apiUrl
axios.defaults.withCredentials = true

axios.interceptors.request.use(
  (config) => {
    const token = persisterStorage.getItem(AUTH_STORAGE_KEY);
    if (token) {
      // Ensure headers is always an object
      config.headers = config.headers || {};
      // Set the Authorization header
      (config.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/admin/refresh')) {
        return Promise.reject(error);
      }
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post('/admin/refresh', {}, {
          withCredentials: true,
        });

        const newToken = response.data?.access_token;

        if (newToken) {
          persisterStorage.setItem(AUTH_STORAGE_KEY, newToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return axios(originalRequest);
        } else {
          throw new Error('No token received from refresh');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        toast.error("Session expired. Please login again.");
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (!error.response) {
      // Handle network errors (no internet, server down, etc.)
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        toast.error("Unable to connect to server. Please check your internet connection.");
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error("Request timed out. Please try again.");
      } else {
        toast.error("Connection failed. Please check your internet connection and try again.");
      }
    } else {
      // Handle HTTP errors with proper status codes
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 400:
          toast.error(data?.message || "Invalid request. Please check your input.");
          break;
        case 401:
          // 401 is already handled by the refresh logic above
          // Only show toast for non-token-expired 401s
          if (data?.detail !== "Token has expired" && data?.detail !== "Invalid token") {
            toast.error(data?.message || "Authentication failed. Please login again.");
          }
          break;
        case 403:
          toast.error("Access denied. You don't have permission to perform this action.");
          break;
        case 404:
          toast.error("The requested resource was not found.");
          break;
        case 422:
          toast.error(data?.message || "Validation error. Please check your input.");
          break;
        case 429:
          toast.error("Too many requests. Please wait a moment and try again.");
          break;
        case 500:
          toast.error("Server error occurred. Please try again later.");
          break;
        case 502:
        case 503:
        case 504:
          toast.error("Service temporarily unavailable. Please try again later.");
          break;
        default:
          toast.error(data?.message || "An unexpected error occurred. Please try again.");
      }
    }

    return Promise.reject(error);
  }
);
